import { FC, useEffect, useState } from 'react';
import s from './QuantityCards.module.scss';
import { usewpdataSelectors } from '@lib/wpdataState/selectors';
import { useproductsdataSelectors } from '@lib/productsState/selectors';
import QuantityCardVariants from './QuantityCardVariants';
import Button from 'components/Button/CTA/Button';
import QuantityIncrementer from '../qtyIncrementer/QuantityIncrementer';
import { useCartActions } from 'lib/cartState/actions';
import { ProductCartItem } from '@lib/cartState/types';
import { useProductsActions } from 'lib/productsState/actions';
import { colorVariant, sizeVariant } from '@lib/productsState/productsTypes';
import ItemsLeft from '../ItemsLeftBar/ItemsLeftBar';
import Divider from '../ContentBlock/Divider';

const QuantityCards: FC = () => {
  const { Settings, hero } = usewpdataSelectors();
  const { Products, SizeVariants, ColorVariants, DefaultVariant } = useproductsdataSelectors();
  const { addMainProductAction, toggleCartAction } = useCartActions();
  const { SetDefaultVariantsAction } = useProductsActions();
  const [selectedProduct, setSelectedProduct] = useState<ProductCartItem | null>(null);

  const currenctPrice = selectedProduct ? selectedProduct?.price * selectedProduct?.amount : 0;

  useEffect(() => {
    if (SizeVariants || ColorVariants) {
      SetDefaultVariantsAction();
    }
  }, [SizeVariants, ColorVariants]);

  useEffect(() => {
    let defaultProductIndex = Products.findIndex((item: ProductCartItem) => item.best_seller === true);
    defaultProductIndex === -1 ? (defaultProductIndex = 0) : defaultProductIndex;

    if (DefaultVariant) {
      setSelectedProduct({
        ...Products[defaultProductIndex],
        type: 'offer',
        variants: DefaultVariant
          ? Array.from({ length: Products[defaultProductIndex].amount }).map(() => DefaultVariant)
          : null,
      });
    }
  }, [DefaultVariant]);

  const handleClick = (product: ProductCartItem) => {
    setSelectedProduct(product);
  };

  const handleSizeChange = (value: string, index: number) => {
    const sizeVariant = SizeVariants?.find((item: sizeVariant) => item?.sku === value);
    if (sizeVariant && selectedProduct?.variants) {
      const variantUpdate = {
        size: sizeVariant.sku,
        sizeLabel: sizeVariant.name,
      };

      setSelectedProduct({
        ...selectedProduct,
        variants: selectedProduct.variants.map((variant, i) => {
          if (i === index) {
            const color = variant.color || '';
            return {
              ...variant,
              ...variantUpdate,
              variantId: `variant_${color}_${sizeVariant.sku}`,
            };
          }
          return variant;
        }),
      });
    }
  };

  const handleColorChange = (value: string, index: number) => {
    const colorVariant = ColorVariants?.find((item: colorVariant) => item?.sku === value);
    if (colorVariant && selectedProduct?.variants) {
      const variantUpdate = {
        color: colorVariant.sku,
        colorLabel: colorVariant.name,
      };

      setSelectedProduct({
        ...selectedProduct,
        variants: selectedProduct.variants.map((variant, i) => {
          if (i === index) {
            const size = variant.size || '';
            return {
              ...variant,
              ...variantUpdate,
              variantId: `variant_${colorVariant.sku}_${size}`,
            };
          }
          return variant;
        }),
      });
    }
  };

  return (
    <section className={s.quantity} id="product-picker">
      {hero?.show_stockLeft && <ItemsLeft />}
      <div style={{ margin: '12px 0' }}>
        <Divider text={hero.divider_text} />
      </div>
      <div className={s.quantity__cardList}>
        {Products &&
          selectedProduct &&
          Products.map((item: ProductCartItem, index: number) => (
            <QuantityCardVariants
              selectedProduct={selectedProduct}
              key={item.id}
              id={item.id}
              index={index}
              active={selectedProduct?.id === item.id}
              amount={item.amount}
              sku={item.sku}
              konnektive_id={item.konnektive_id}
              display_name={item.display_name}
              generic_name={item.generic_name}
              thank_you_displayed_name={item?.thank_you_displayed_name || ''}
              description={item.description}
              free_product_step={item.free_product_step || false}
              price={item.price}
              image={item.image}
              old_price={item.old_price}
              best_seller={item.best_seller || false}
              best_value={item.best_value || false}
              free_products={item.free_products || []}
              handleClick={handleClick}
              handleColorChange={handleColorChange}
              handleSizeChange={handleSizeChange}
            />
          ))}
      </div>

      <div className={`${s.ctaWrapper} ${Settings?.showQtyIncrementer ? s.ctaWrapper__bundle : ''}`}>
        {Settings?.showQtyIncrementer && <QuantityIncrementer />}
        <Button
          handleClick={() => {
            if (selectedProduct) {
              addMainProductAction(selectedProduct);
              toggleCartAction();
            }
          }}
          text={`${hero?.ctaButtonText} ${Settings.showPriceInCTA ? `| ${currenctPrice.toFixed(2)}` : ''}`}
          className={s.ctaButton}
        />
      </div>
    </section>
  );
};

export default QuantityCards;
